<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import Bar from '@/components/common/dashboard/chart/Bar.vue'
import { getThisYear, numberFormatter } from '@/js/customChart'
import { getPipelineStatusList, getPipelineStatusGoodsList } from '@/api/dashboard'

import { useNavigatorStore } from '@/store/navigator'
const navigatorStore = useNavigatorStore()
import { useUserStore } from '@/store/user'
const userStore = useUserStore()

// props
const props = defineProps({
  chartType: String,
  employeeId: String,
  legend: String,
  width: Number,
  height: Number,
  title: String,
  data: Object,
  fiscalYear:String
})

// data
const fetchedData = ref()

const APIS = {
  opportunity: getOpportunity,
}

// computed
const chartOptions = computed(() => {
  const CHART_OPTIONS = {
    aspectRatio: navigatorStore.isMobile ? 1.3 : 2,
    devicePixelRatio: 1,
    indexAxis: "y",
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          stepSize: fetchedData.value?.additionalData.ref || 1,
          callback: function (value, index, ticks) {
            return `${index.toFixed(1)}x`;
          }
        }
      }
    },
    responsive: true,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        usePointStyle: true,
        callbacks: {
          labelPointStyle() {
            return {
              pointStyle: false,
            }
          },
          title([data]) {
            return ''
          },
          label(data) {
            return fetchedData.value.additionalData.tooltip[data.label] || ''
          }
        }
      }
    },
  }
  return CHART_OPTIONS
})

// watch
watch([() => props.employeeId, () => props.legend, ()=>props.fiscalYear], async newValue => {
  const [employeeId, legend, fiscalYear] = newValue
  try {
    await APIS['opportunity'](employeeId,fiscalYear)
  } catch (err) {
    console.error(err)
  }
})

// lifecycle
onMounted(async () => {
  const employeeId = userStore.isAdmin ? '' : userStore.user.employeeId
  try {
    await APIS['opportunity'](props.employeeId || employeeId,props.fiscalYear)
  } catch (err) {
    console.error(err)
  }
})

function getMappedData(result) {
  const axisY = [
    "Pipeline",
    "Upside",
    "Commit",
    "WonClose",
    "Quota"
  ]
  const groupByResult = Object.groupBy(result.rtn, ({ wontitle }) => wontitle)
  const sumValues = Object.fromEntries(Object.entries(groupByResult)
    .map(([key, value]) => {
      return [key, value.find(item => item.rank === '1').allsumdecimal]
    }))
  return {
    axisY,
    groupByResult,
    sumValues,
  }
}

async function getOpportunity(employeeId,fiscalYear) {
  try {
    const result = await getPipelineStatusList({
      fs_year: fiscalYear,
      employee_id: employeeId
    })
    const { axisY, groupByResult, sumValues } = getMappedData(result)
    function accumulator(level) {
      const target = [...axisY].reverse().filter((title, index) => title !== 'Quota' && index <= level)
      return Object.entries(sumValues)
        .filter(([key]) => target.includes(key))
        .map(([, value]) => {
          return parseFloat(value)
        }) 
        .reduce((prev, curr) => prev + curr, 0)
    }
    fetchedData.value = {
      data: {
        labels: [...axisY],
        datasets: [
          {
            data: [
              [accumulator(3), accumulator(4)],
              [accumulator(2), accumulator(3)],
              [accumulator(1), accumulator(2)],
              [0, accumulator(1)],
              [0, sumValues.Quota],
            ],
            backgroundColor: [
              "rgba(54, 162, 235, 0.8)",
              "rgba(255, 100, 86, 0.8)",
              "rgba(255, 200, 86, 0.8)",
              "rgba(54, 162, 235, 0.8)",
              "rgba(200, 200, 200, 0.8)"
            ]
          }
        ]
      },
      additionalData: {
        ref: sumValues.Quota || 0,
        values: [...axisY].map(title => sumValues[title] || 0),
        ratio: [
          isNaN((accumulator(4) / sumValues.Quota).toFixed(1)) ? 0 : (accumulator(4) / sumValues.Quota).toFixed(1),
          isNaN((accumulator(3) / sumValues.Quota).toFixed(1)) ? 0 : (accumulator(3) / sumValues.Quota).toFixed(1),
        ],
        tooltip: {
          ...Object.fromEntries(Object.entries(groupByResult).map(([key, value]) => {
            const mappedValue = value.map((item, index) => {
              return `${index + 1}. ${item.end_user_cmp_nm} ${item.sales_nm} ${numberFormatter(item.wonclosesumdecimal)}억원`
            })
            return [key, mappedValue]
          }))
        }
      }
    }
  } catch (err) {
    console.error(err)
  }
}

</script>

<template>
  <Bar :width="width" :height="height" :data="fetchedData?.data" :additionalData="fetchedData?.additionalData"
    :title="title" :options="chartOptions" />
</template>